import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { COCKTAILS } from "../data/content";
import PogLogo from "./PogLogo";
import ShareBar from "./ShareBar";

const Cocktails = () => {
  return (
    <section
      id="cocktails"
      data-testid="cocktails-section"
      className="relative py-32 md:py-56 bg-[#1E1712] text-[#EFE7D6] overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-8 md:px-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="mb-20 md:mb-28"
        >
          <div className="flex items-center gap-6 mb-10">
            <span className="counter">06 — Serves</span>
            <span className="w-16 hairline-strong h-px inline-block" />
            <span className="overline">Mixology</span>
          </div>
          <h2 className="display-tight text-[3rem] sm:text-6xl md:text-8xl max-w-5xl">
            Three pours
            <br />
            <span className="italic font-light text-[#DCCFB4]">for the islands.</span>
          </h2>
          <p className="font-body text-xl md:text-2xl text-[#DCCFB4]/80 mt-10 max-w-2xl leading-[1.5]">
            Signature serves crafted to honor the original — equally at home on
            a private lānai or behind the bar at the Halekulani.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-14 md:gap-10">
          {COCKTAILS.map((c, i) => (
            <motion.article
              key={c.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="group"
              data-testid={`cocktail-${c.id}`}
            >
              <div
                className="relative aspect-[3/4] overflow-hidden mb-8"
                style={{
                  background:
                    "radial-gradient(ellipse at 50% 45%, #2a1f18 0%, #0f0a07 80%)",
                }}
              >
                <div className="absolute inset-0 opacity-20 sunburst-ghost pointer-events-none" />
                <motion.img
                  src={c.image}
                  alt={`${c.name} — POG Spirits cocktail recipe`}
                  loading="lazy"
                  whileHover={{ scale: 1.04 }}
                  transition={{ duration: 1.4, ease: "easeOut" }}
                  className="absolute inset-0 w-full h-full object-contain p-8 drop-shadow-[0_20px_50px_rgba(0,0,0,0.55)]"
                />
                <div className="absolute top-5 left-5 z-10 flex items-center gap-3">
                  <span className="counter text-[#EFE7D6]/60">0{i + 1}</span>
                </div>
                <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between z-10">
                  <span className="overline text-[#EFE7D6]/70 text-[10px]">
                    {c.glass}
                  </span>
                  <PogLogo size={22} />
                </div>
              </div>

              <h3 className="display-tight text-3xl md:text-4xl mb-6">
                {c.name}
              </h3>

              <div className="overline mb-4">Build</div>
              <ul className="space-y-2 mb-8 border-t border-[#EFE7D6]/12 pt-4">
                {c.ingredients.map((ing) => (
                  <li
                    key={ing}
                    className="font-body text-lg text-[#EFE7D6]/90 leading-relaxed"
                  >
                    — {ing}
                  </li>
                ))}
              </ul>

              <div className="overline mb-3">Method</div>
              <p className="font-body text-[#DCCFB4]/80 leading-[1.75] text-base font-light">
                {c.method}
              </p>

              <div className="mt-6">
                <ShareBar
                  url={`https://pogspirits.com/cocktails/${{ sunset: "kailua-sunset", lei: "lei-day-spritz", punch: "big-island-punch" }[c.id]}`}
                  title={`${c.name} — POG Spirits cocktail recipe`}
                  testid={`cocktail-${c.id}-share`}
                />
              </div>
            </motion.article>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-20 text-center"
        >
          <Link
            to="/cocktails"
            data-testid="cocktails-gallery-link"
            className="inline-block px-10 py-5 border border-[#EFE7D6]/30 text-sm tracking-[0.25em] uppercase font-body hover:border-[#F26B1F] hover:text-[#F26B1F] transition-colors duration-300"
          >
            Explore the full gallery — 6 recipes
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Cocktails;
